import {
	MouseEvent,
	ReactElement,
	Ref,
	useCallback,
	useRef,
	DragEvent,
	forwardRef,
	useImperativeHandle,
	KeyboardEvent,
	useMemo,
	useState
} from "react";
import classNames from "classnames";
import {
	Checkbox,
	DateString,
	Icon,
	FontIconName,
	PersonAvatar,
	QuickActions,
	NavigationLink
} from "client/Components";
import {
	ItemSelectionKind,
	SelectableItemOptions,
	useLongTouch
} from "client/Hooks";
import { EmailImportance } from "client/Api/Email/common";
import { EmailActions } from "client/Api/Email/email";
import { EmailListItemWithMailbox } from "../ListView";
import { localize } from "client_legacy/ndaw/culture";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import styles from "./Item.module.less";
import { setTrackingId } from "client/Misc/utils";
import {
	EmailDragStartCallbackData,
	EMAIL_DRAGGABLE_DATA_IDENTIFIER
} from "../../dragAndDrop";
import { Coordinate, Key } from "client/Misc/commonTypes";
import { getComputedDisplayName, getContactPhotoUrl } from "client/Features/AddressBook/common";
import {
	EmailActionModel,
	EmailSelectionAction,
	EmailSelectionActionContext
} from "../../Actions/selectionActions";
import { Navigation } from "client/Navigation";
import { emailRouting } from "../../Email";
import { TimeZone } from "client/DateAndTime/timezones";

export type ItemHandle = {
	focus(): void;
};

export type ItemProps = {
	model: EmailListItemWithMailbox;
	isOpened: boolean;
	selectable?: SelectableItemOptions;
	selectionActions: readonly EmailSelectionAction[];
	isTouch: boolean;
	isInSelectMode: boolean;
	isContextMenuDisabled: boolean;
	hasSelectedItems: boolean;
	isInSentFolder: boolean;
	onEnableSelectMode(): void;
	canOpenEmail(): boolean;
	onDragStart(initiatorEmailInfo: EmailIdWithIsReadStatus): EmailDragStartCallbackData;

	onKeyboardNextItem(isShift: boolean): void;
	onKeyboardPreviousItem(isShift: boolean): void;

	onKeyboardNextPageItem(isShift: boolean): void;
	onKeyboardPreviousPageItem(isShift: boolean): void;

	onKeyboardFirstItem(isShift: boolean): void;
	onKeyboardLastItem(isShift: boolean): void;

	onDelete(): void;
	onSelectOpenedEmail(): void;
	onOpenEmail(): void;
	onContextMenu: (coordinate: Coordinate) => void;
	timezone: TimeZone;
};

export const Item = forwardRef(
	(props: ItemProps, ref: Ref<ItemHandle>): ReactElement => {
		const {
			model,
			isOpened,
			selectable,
			selectionActions,
			isTouch,
			isInSelectMode,
			isContextMenuDisabled,
			hasSelectedItems,
			isInSentFolder,
			onEnableSelectMode,
			canOpenEmail,
			onDragStart,
			onKeyboardNextItem,
			onKeyboardPreviousItem,
			onKeyboardNextPageItem,
			onKeyboardPreviousPageItem,
			onKeyboardFirstItem,
			onKeyboardLastItem,
			onDelete,
			onSelectOpenedEmail,
			onOpenEmail,
			onContextMenu,
			timezone
		} = props;

		const [isMoreActionsOpen, setIsMoreActionOpen] = useState<boolean>(false);

		const {
			emailId,
			subject,
			lastAction,
			hasAttachments,
			isFlagged,
			importance,
			sentDateTime,
			receivedDateTime,
			ownerName,
			isRead,
			isMeetingMessage,
			isDraft,
			from,
			preview,
			smtpAddress
		} = model;

		const hasReply: boolean = lastAction === EmailActions.Replied;
		const hasForward: boolean = lastAction === EmailActions.Forwarded;
		const isHighImportance: boolean = importance === EmailImportance.High;
		const isLowImportance: boolean = importance === EmailImportance.Low;
		const anchorRef: Ref<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);

		const longTouchHandler = useCallback(
			(): void => {
				if (selectable) {
					selectable.onSelect(ItemSelectionKind.Override);
					onEnableSelectMode();
				}
			},
			[onEnableSelectMode, selectable]
		);

		const {
			onTouchStart,
			onTouchMove,
			onTouchEnd,
			isActive: isLongTouchActive
		} = useLongTouch({ handler: longTouchHandler });

		const quickActionsContext = useMemo(
			(): EmailSelectionActionContext => {
				const thisItem: EmailActionModel = {
					emailId,
					subject,
					isDraft,
					isFlagged,
					isRead,
					smtpAddress
				};

				return {
					effectiveSelection: [thisItem],
					selectedItems: []
				};
			},
			[
				emailId,
				isDraft,
				isFlagged,
				isRead,
				subject,
				smtpAddress
			]
		);

		const dragStartHandler = useCallback(
			(event: DragEvent): void => {
				const {
					draggableData,
					tooltip
				} = onDragStart({
					emailId: model.emailId,
					isRead: model.isRead
				});
				const serializedData: string = JSON.stringify(draggableData);
				event.dataTransfer.effectAllowed = "move";
				event.dataTransfer.setData(EMAIL_DRAGGABLE_DATA_IDENTIFIER, serializedData);
				event.dataTransfer.setDragImage(tooltip, -12, -12);
			},
			[model, onDragStart]
		);

		const clickHandler = useCallback(
			(event: MouseEvent) => {
				if (isLongTouchActive) {
					event.preventDefault();
					return;
				}

				if (selectable) {
					if (event.ctrlKey || event.shiftKey) {
						if (!isOpened && !hasSelectedItems) {
							onSelectOpenedEmail();
						}
					}

					if (isInSelectMode || event.ctrlKey) {
						event.preventDefault();
						selectable.onSelect(ItemSelectionKind.Append);
					} else if (event.shiftKey) {
						event.preventDefault();
						selectable.onSelect(ItemSelectionKind.RangeFromPrevious);
					} else {
						if (!canOpenEmail()) {
							event.preventDefault();
						}

						anchorRef.current
							?.focus();
					}
				} else {
					event.preventDefault();
				}
			},
			[
				isLongTouchActive,
				isInSelectMode,
				isOpened,
				hasSelectedItems,
				onSelectOpenedEmail,
				canOpenEmail,
				selectable
			]
		);

		const contextMenuHandler = useCallback(
			(args: MouseEvent) => {
				args.preventDefault();

				if (isLongTouchActive) {
					return;
				}

				if (selectable
					&& !selectable.isSelected
					&& !isOpened
				) {
					onOpenEmail();
				}

				onContextMenu({
					x: args.clientX,
					y: args.clientY
				});
			},
			[
				isLongTouchActive,
				isOpened,
				onContextMenu,
				onOpenEmail,
				selectable
			]
		);

		const optionalContextMenuHandler = useMemo(
			(): ((args: MouseEvent) => void) => {
				if (isContextMenuDisabled) {
					return (event: MouseEvent): void => {
						event.preventDefault();
					};
				}

				return contextMenuHandler;
			},
			[
				contextMenuHandler,
				isContextMenuDisabled
			]
		);

		const keyDownHandler = (event: KeyboardEvent<HTMLAnchorElement>): void => {
			const key: Key | null = tryResolveKeyboardKey(event);
			let shouldPreventDefault: boolean = true;

			if (event.ctrlKey) {
				return;
			}

			switch (key) {
				case Key.ArrowUp:
					onKeyboardPreviousItem(event.shiftKey);
					break;
				case Key.ArrowDown:
					onKeyboardNextItem(event.shiftKey);
					break;
				case Key.PageUp:
					onKeyboardPreviousPageItem(event.shiftKey);
					break;
				case Key.PageDown:
					onKeyboardNextPageItem(event.shiftKey);
					break;
				case Key.Home:
					onKeyboardFirstItem(event.shiftKey);
					break;
				case Key.End:
					onKeyboardLastItem(event.shiftKey);
					break;
				case Key.Delete:
					onDelete();
					break;
				default:
					shouldPreventDefault = false;
					break;
			}

			if (shouldPreventDefault) {
				event.preventDefault();
			}
		};

		const itemNavigation = useMemo(
			(): Navigation => {
				return emailRouting.page({
					queryParams: isDraft
						? {
							draftIdToEdit: emailId.id,
							mailboxToOpen: smtpAddress ?? undefined
						}
						: {
							emailToOpen: emailId.id,
							mailboxToOpen: smtpAddress ?? undefined
						}
				});
			},
			[
				emailId.id,
				isDraft,
				smtpAddress
			]
		);

		useImperativeHandle(
			ref,
			(): ItemHandle => ({
				focus(): void {
					anchorRef.current
						?.focus();
				}
			}),
			[]
		);

		const checkboxClickHandler = useCallback(
			(): void => {
				if (!selectable) {
					return;
				}

				selectable.onSelect(ItemSelectionKind.Append);
				anchorRef.current
					?.focus();
			},
			[
				selectable
			]
		);

		const {
			initials
		} = getComputedDisplayName (
			{
				displayName: ownerName
			}
		);

		const isSelected: boolean | undefined = selectable && selectable.isSelected;
		const hasCheckbox: boolean = isInSelectMode
			|| hasSelectedItems
			|| isOpened
			|| Boolean(isSelected);

		const deliveryDateTime: string | null = isInSentFolder
			? sentDateTime
			: receivedDateTime;

		return (
			<div
				{...setTrackingId(`list-item|${model.emailId.id}`)}
				className={classNames(
					styles.root,
					{
						[styles.root_selected]: isSelected,
						[styles.root_isOpen]: isOpened && !hasSelectedItems,
						[styles.root_hasCheckbox]: hasCheckbox,
						[styles.root_read]: isRead,
						[styles.root_isMoreActionsOpen]: isMoreActionsOpen
					}
				)}
				aria-selected={isInSelectMode}
				onContextMenu={optionalContextMenuHandler}
				draggable
				onDragStart={dragStartHandler}
			>
				<div className={styles.prefix}>
					<div className={styles.prefixContainer}>
						<div className={styles.avatar}>
							<PersonAvatar
								initials={initials}
								imageSrc={getContactPhotoUrl(from)}
								size="x-small"
							/>
						</div>
						<div className={styles.checkbox}>
							{selectable
								? (
									<Checkbox
										value={selectable.isSelected}
										onChange={checkboxClickHandler}
										trackingId="checkbox"
									/>
								)
								: (
									<Checkbox
										value={false}
										isDisabled
										onChange={() => null}
										trackingId="checkbox"
									/>
								)
							}
						</div>
					</div>
				</div>
				<NavigationLink
					navigation={itemNavigation}
					className={styles.content}
					ref={anchorRef}
					onClick={clickHandler}
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
					onTouchMove={onTouchMove}
					tabIndex={-1}
					onKeyDown={keyDownHandler}
					trackingId="content"
				>
					<div className={styles.header}>
						<h4
							{...setTrackingId("user-name")}
							className={styles.userName}
						>
							{ownerName}
						</h4>
						<div
							{...setTrackingId("icons-container")}
							className={styles.iconsContainer}
						>
							{hasReply && <EmailIcon iconName="reply" trackingId="has-reply" />}
							{hasForward && <EmailIcon iconName="share" trackingId="has-forward" />}
							{isMeetingMessage && <EmailIcon iconName="calendar" trackingId="is-meeting" />}
							{hasAttachments && <EmailIcon iconName="paperclip" trackingId="has-attachments" />}
							{isFlagged && <EmailIcon iconName="flag-filled" isPrimary trackingId="is-flagged" />}
							{isLowImportance && <EmailIcon iconName="arrow-down" trackingId="is-low-importance" />}
							{isHighImportance && <EmailIcon iconName="triangle-exclamation" trackingId="is-high-importance" />}
						</div>
						<div className={styles.actions}>
							<QuickActions
								actions={selectionActions}
								actionContext={quickActionsContext}
								isTouch={isTouch}
								isMoreActionsOpen={isMoreActionsOpen}
								onIsMoreActionOpenChange={setIsMoreActionOpen}
								menuTitle={subject || localize("_NoSubject")}
							/>
						</div>
					</div>
					<div className={styles.title}>
						<h4
							{...setTrackingId("subject")}
							className={styles.subject}
						>
							{subject || localize("_NoSubject")}
						</h4>
						<div {...setTrackingId("date")} className={styles.date}>
							{deliveryDateTime && (
								<DateString
									value={deliveryDateTime}
									format="AbsoluteTimestamp"
									timezone={timezone}
								/>
							)}
						</div>
					</div>
					<div {...setTrackingId("from")} className={styles.preview}>
						{preview || localize("_NoPreviewAvailable")}
					</div>
				</NavigationLink>
			</div>
		);
	}
);
Item.displayName = "Item";

type EmailIconProps = {
	iconName: FontIconName;
	trackingId?: string;
	isPrimary?: boolean;
};

const EmailIcon = (props: EmailIconProps): ReactElement => {
	const {
		iconName,
		isPrimary,
		trackingId
	} = props;

	return (
		<span {...setTrackingId(trackingId)} className={styles.icon}>
			<Icon
				value={{
					kind: "font",
					name: iconName,
					color: isPrimary ? "primary" : "secondary"
				}}
			/>
		</span>
	);
};
EmailIcon.displayName="EmailIcon";